import { useState } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import MuiTimeline from '@mui/lab/Timeline'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import type { TimelineProps } from '@mui/lab/Timeline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'

import type { ConnectorKey, ConnectorProbeStatus, ProbeStep } from '@server/domain/connector-probe'

import CustomChip from '@core/components/mui/chip'
import CustomTabList from '@core/components/mui/tab-list'
import type { ThemeColor } from '@core/types'
import DialogCloseButton from '@components/dialogs/dialog-close-button'

import { useTRPC } from '@/lib/trpc'
import { requireAdmin } from '@/lib/route-guards'
import { formatDateTime } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/admin/connectors')({
  beforeLoad: requireAdmin,
  component: Connectors
})

/** How often the screen reads a probe back while it runs. */
const POLL_MS = 2_000

const STATUS: Record<ConnectorProbeStatus, { label: string; color: ThemeColor }> = {
  queued: { label: 'Na fila', color: 'secondary' },
  running: { label: 'Rodando', color: 'info' },
  passed: { label: 'Chegou ao fim', color: 'success' },
  failed: { label: 'Parou no meio', color: 'error' },
  canceled: { label: 'Parado por uma pessoa', color: 'warning' }
}

const DOT: Record<ProbeStep['outcome'], 'success' | 'error' | 'info'> = {
  ok: 'success',
  failed: 'error',
  info: 'info'
}

/** What each rehearsal does — and that none of them buys anything. */
const CONNECTORS: { key: ConnectorKey; tab: string; title: string; description: string }[] = [
  {
    key: 'ridigital',
    tab: 'RI Digital',
    title: 'RI Digital — Pesquisa Nacional de Bens',
    description:
      'Entra no RI Digital com o certificado A1 do escritório, abre a Pesquisa Nacional de Bens e vai até o resumo com o preço. Não paga nem envia nenhum pedido.'
  },
  {
    key: 'censec',
    tab: 'CENSEC',
    title: 'CENSEC — Busca de escrituras e procurações',
    description:
      'Entra na Busca CEP pelo e-Notariado com o certificado A1, abre a consulta e preenche CPF/CNPJ e nome. Nunca clica em "Realizar Consulta", que gasta saldo.'
  }
]

const Timeline = styled(MuiTimeline)<TimelineProps>({
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiTimelineItem-root': {
    width: '100%',
    '&:before': {
      display: 'none'
    }
  }
})

/** The connector rehearsals: each logs in, reaches the order form, and stops before spending. */
function Connectors() {
  const [enlarged, setEnlarged] = useState<string | null>(null)
  // One connector at a time: a finished run with its screenshots fills the screen.
  const [tab, setTab] = useState<ConnectorKey>(CONNECTORS[0].key)

  return (
    <div className='flex flex-col gap-6'>
      <TabContext value={tab}>
        <CustomTabList variant='scrollable' scrollButtons='auto' onChange={(_, value: ConnectorKey) => setTab(value)}>
          {CONNECTORS.map(connector => (
            <Tab key={connector.key} value={connector.key} label={connector.tab} />
          ))}
        </CustomTabList>

        {CONNECTORS.map(connector => (
          <TabPanel key={connector.key} value={connector.key} className='p-0'>
            <ProbeCard
              connector={connector.key}
              title={connector.title}
              description={connector.description}
              onEnlarge={setEnlarged}
            />
          </TabPanel>
        ))}
      </TabContext>

      <Dialog fullWidth maxWidth='lg' open={enlarged !== null} onClose={() => setEnlarged(null)} closeAfterTransition={false}>
        <DialogCloseButton onClick={() => setEnlarged(null)} disableRipple>
          <i className='tabler-x' />
        </DialogCloseButton>
        <DialogContent>
          {enlarged && <img alt='Tela do teste' src={`data:image/jpeg;base64,${enlarged}`} className='is-full' />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ProbeCard({
  connector,
  title,
  description,
  onEnlarge
}: {
  connector: ConnectorKey
  title: string
  description: string
  onEnlarge: (screenshot: string) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const probe = useQuery(
    trpc.operations.probe.queryOptions(
      { connector },
      { refetchInterval: query => (query.state.data && ['queued', 'running'].includes(query.state.data.status) ? POLL_MS : false) }
    )
  )

  const request = useMutation(
    trpc.operations.requestProbe.mutationOptions({
      onSuccess: started => {
        queryClient.setQueryData(trpc.operations.probe.queryKey({ connector }), started)
        toast.success('Teste pedido — o passo a passo aparece abaixo.')
      },
      onError: error => toast.error(error.message)
    })
  )

  const cancel = useMutation(
    trpc.operations.cancelProbe.mutationOptions({
      onSuccess: stopped => {
        queryClient.setQueryData(trpc.operations.probe.queryKey({ connector }), stopped)
        toast.info('Teste parado.')
      },
      onError: error => toast.error(error.message)
    })
  )

  const current = probe.data ?? null
  const open = current ? ['queued', 'running'].includes(current.status) : false
  const busy = open && !current?.stranded
  const pending = request.isPending || cancel.isPending

  return (
    <Card>
      <CardHeader
        title={title}
        subheader={description}
        action={
          <div className='flex flex-wrap gap-2'>
            {busy ? (
              <>
                <Button
                  variant='tonal'
                  color='error'
                  disabled={pending}
                  startIcon={<i className='tabler-player-stop' />}
                  onClick={() => cancel.mutate({ connector })}
                >
                  Parar
                </Button>
                <Button
                  variant='tonal'
                  disabled={pending}
                  startIcon={<i className='tabler-refresh' />}
                  onClick={() => request.mutate({ connector, restart: true })}
                >
                  Reiniciar
                </Button>
              </>
            ) : (
              <Button
                variant='contained'
                disabled={pending}
                startIcon={<i className='tabler-player-play' />}
                onClick={() => request.mutate({ connector, restart: false })}
              >
                {current ? 'Testar de novo' : 'Testar'}
              </Button>
            )}
          </div>
        }
      />
      <CardContent className='flex flex-col gap-4'>
        {!current && <Typography color='text.secondary'>Nenhum teste feito ainda.</Typography>}

        {current && (
          <div className='flex flex-wrap items-center gap-3'>
            <CustomChip size='small' variant='tonal' color={STATUS[current.status].color} label={STATUS[current.status].label} />
            <Typography variant='body2' color='text.secondary'>
              Pedido em {formatDateTime(current.requestedAt)}
              {current.finishedAt ? ` · terminou em ${formatDateTime(current.finishedAt)}` : ''}
            </Typography>
          </div>
        )}

        {current?.stranded && (
          <Alert severity='warning'>
            Ninguém pegou este teste. O serviço <strong>ridigital-probe</strong> não está rodando no servidor — confira no
            Dokploy.
          </Alert>
        )}

        {current && current.steps.length > 0 && (
          <Timeline>
            {current.steps.map((step, index) => (
              <TimelineItem key={`${step.at}-${index}`}>
                <TimelineSeparator>
                  <TimelineDot color={DOT[step.outcome]} />
                  {index < current.steps.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <div className='flex flex-wrap items-center justify-between gap-x-2 mbe-1'>
                    <Typography className='font-medium' color='text.primary'>
                      {step.label}
                    </Typography>
                    <Typography variant='caption'>{formatDateTime(step.at)}</Typography>
                  </div>
                  {step.detail && (
                    <Typography variant='body2' className='mbe-2 whitespace-pre-wrap'>
                      {step.detail}
                    </Typography>
                  )}
                  {step.screenshot && (
                    <img
                      alt={step.label}
                      src={`data:image/jpeg;base64,${step.screenshot}`}
                      className='is-full max-is-[480px] cursor-zoom-in rounded border'
                      onClick={() => onEnlarge(step.screenshot!)}
                    />
                  )}
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}

        {busy && current && current.steps.length === 0 && (
          <Typography color='text.secondary'>Esperando o serviço de teste pegar o pedido…</Typography>
        )}
      </CardContent>
    </Card>
  )
}
